/**
 * ScreenShareGate
 * ================
 *
 * Mandatory-share modal for agents whose backend `should-record` returns
 * enabled=true. Renders a full-screen blocking overlay until the agent
 * grants screen-share permission.
 *
 * Design (2026-07-15):
 *   * Auto-checks `/api/screen-recordings/should-record` on mount.
 *   * If enabled and no live screen stream yet:
 *       - Renders a modal explaining that screen recording is a
 *         REQUIRED part of the agent workflow.
 *       - Big "Enable Screen Recording" button that calls
 *         `window.__cxScreenRec.acquireStream(...)`.
 *       - Escape hatch: "Log out" button; no way to dismiss without
 *         either sharing or logging out.
 *   * When the stream is live, the gate hides itself.
 *   * If the agent stops the share via the browser-native indicator,
 *     `useScreenRecording.releaseStream()` fires and the gate re-appears
 *     on the next render.
 *
 * We DON'T mount our own `useScreenRecording` here \u2014 that hook is
 * mounted once inside AgentStatusBar and publishes `window.__cxScreenRec`.
 * We just consume that handle. This keeps a single MediaStream in the app
 * (avoiding two getDisplayMedia prompts).
 */
import React, { useEffect, useState } from 'react';
import { screenRecordingsApi } from '../../api/client';

// Poll `window.__cxScreenRec.getState()` at this interval to detect a
// stream release via the browser-native "Stop sharing" indicator.
const POLL_INTERVAL_MS = 1000;

export default function ScreenShareGate({ extension, onLogout, onBlock, onBypass }) {
  const [required, setRequired] = useState(null);   // null | true | false
  const [hasStream, setHasStream] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // 2026-07-28 connectivity preflight state.
  // conn: null (untested) | 'testing' | 'ok' | 'failed'
  const [conn, setConn] = useState(null);
  const [connError, setConnError] = useState(null);
  const [bypassAllowed, setBypassAllowed] = useState(false);
  const [bypassed, setBypassed] = useState(false);

  // Multi-monitor (2026-09-10): opt-in second-screen prompt (no surprise
  // double browser prompt). Shows a small non-blocking banner once the primary
  // screen is live + connectivity ok, IF this agent may record >1 screen.
  const [maxScreens, setMaxScreens] = useState(1);
  const [extraCount, setExtraCount] = useState(0);
  const [secondScreenDismissed, setSecondScreenDismissed] = useState(false);
  const [addingScreen, setAddingScreen] = useState(false);

  // 1) Query backend for whether this agent must record. COMPLIANCE:
  //    FAIL CLOSED. If we can't determine the requirement (API error), we
  //    assume it IS required and block, then RETRY — rather than the old
  //    'permissive' behavior which silently let agents (e.g. 60200 on
  //    2026-07-27) take 16 calls with zero recording. The only way past the
  //    gate is a confirmed enabled=false OR a live screen stream.
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const probe = async () => {
      if (!extension) {
        // No extension yet -> can't confirm not-required. Keep checking;
        // don't permanently unblock on a transient missing extension.
        if (!cancelled) retryTimer = setTimeout(probe, 3000);
        return;
      }
      try {
        const resp = await screenRecordingsApi.shouldRecord({
          agent_extension: extension,
        });
        if (!cancelled) {
          setRequired(!!resp?.enabled);
          setBypassAllowed(!!resp?.bypass_allowed);
        }
      } catch (err) {
        // FAIL CLOSED: block + retry until we get a definitive answer.
        console.warn('[CX-SR-GATE] should-record failed -> BLOCK + retry (fail-closed)', err);
        if (!cancelled) {
          setRequired(true);
          retryTimer = setTimeout(probe, 5000);
        }
      }
    };
    probe();
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [extension]);

  // 2) Poll the hook's stream state so we can hide the gate as soon as
  //    the agent grants permission.
  //
  // 2026-07-15 fix: at end of call the hook briefly goes through
  // 'stopping' before returning to 'permissioned', and during a
  // per-call recording it's in 'recording'. Both mean the stream is
  // still live — do NOT re-prompt. Only re-prompt when the stream is
  // genuinely gone (idle) or was denied.
  //
  // States (from useScreenRecording):
  //   idle | preparing | permissioned | recording | stopping | denied | error
  //
  // "Live stream present" =  anything except idle / denied.
  // 'preparing' happens before permission is asked. If we hit that
  // path here it means acquireStream is in flight (from the button
  // click) — don't re-render the modal on top of itself.
  // 'error' generally means a recorder/chunk failure but the stream
  // is still permissioned; the reaper will handle abort.
  useEffect(() => {
    const tick = () => {
      try {
        const sr = window.__cxScreenRec;
        if (!sr) { setHasStream(false); return; }
        const s = sr.getState?.();
        const streamAlive = s && s !== 'idle' && s !== 'denied';
        setHasStream(!!streamAlive);
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      const sr = window.__cxScreenRec;
      if (!sr) throw new Error('screen recording not initialized');
      const ok = await sr.acquireStream({ agentExtension: extension || null });
      if (!ok) {
        setError('Permission was denied. Please click again and choose an entire screen (not a single tab).');
      }
    } catch (err) {
      setError(err?.message || 'Failed to enable screen sharing.');
    } finally {
      setBusy(false);
    }
  };

  // Multi-monitor (2026-09-10): poll how many screens this agent may record +
  // how many extras are currently captured, so we can offer the opt-in button.
  useEffect(() => {
    const tick = () => {
      try {
        const sr = window.__cxScreenRec;
        if (!sr) return;
        if (sr.maxScreens) setMaxScreens(Math.max(1, sr.maxScreens()));
        if (sr.extraScreenCount) setExtraCount(sr.extraScreenCount());
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  const handleAddScreen = async () => {
    setAddingScreen(true);
    try {
      const sr = window.__cxScreenRec;
      const ok = sr?.acquireExtraScreen ? await sr.acquireExtraScreen() : false;
      if (ok && sr?.extraScreenCount) setExtraCount(sr.extraScreenCount());
    } catch (_) { /* best-effort */ }
    finally { setAddingScreen(false); }
  };

  // 2026-07-28: run the Azure connectivity preflight. Called automatically
  // once we have a live stream (and required), and by the Retry button.
  const runPreflight = React.useCallback(async () => {
    setConn('testing');
    setConnError(null);
    try {
      const sr = window.__cxScreenRec;
      const fn = sr?.runAzurePreflight;
      if (!fn) {
        // Hook not ready yet; leave untested so the effect retries.
        setConn(null);
        return;
      }
      const res = await fn(extension || null);
      if (res?.ok) {
        setConn('ok');
      } else {
        setConn('failed');
        setConnError(res?.error || 'connectivity_failed');
        if (typeof res?.bypassAllowed === 'boolean') {
          setBypassAllowed(res.bypassAllowed);
        }
        console.warn('[CX-SR-GATE] Azure preflight FAILED', res);
      }
    } catch (err) {
      setConn('failed');
      setConnError(String(err?.message || err));
    }
  }, [extension]);

  // Auto-run the preflight when required + stream present + not yet tested.
  useEffect(() => {
    if (required === true && hasStream && conn === null && !bypassed) {
      runPreflight();
    }
  }, [required, hasStream, conn, bypassed, runPreflight]);

  const handleBypass = async () => {
    setBusy(true);
    try {
      await screenRecordingsApi.bypass({
        agent_extension: extension || null,
        preflight_error: connError || 'connectivity_failed',
      });
      setBypassed(true);
      try { onBypass && onBypass(); } catch (_) {}
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to record bypass.');
    } finally {
      setBusy(false);
    }
  };

  // COMPLIANCE ENFORCEMENT: while the gate is BLOCKING, force the agent out
  // of Ready so NO calls route to them unrecorded. Blocking now covers BOTH
  // "no screen stream" AND "stream present but Azure connectivity failed and
  // not bypassed". Fires whenever we transition into the blocking state.
  const connBlocking = required === true && hasStream && conn === 'failed' && !bypassed;
  const blocking = required === true && (!hasStream || connBlocking);
  const wasBlockingRef = React.useRef(false);
  useEffect(() => {
    if (blocking && !wasBlockingRef.current) {
      wasBlockingRef.current = true;
      console.warn('[CX-SR-GATE] BLOCKING: forcing agent to not-ready');
      try { onBlock && onBlock(); } catch (_) {}
    } else if (!blocking) {
      wasBlockingRef.current = false;
    }
  }, [blocking, onBlock]);

  // Multi-monitor (2026-09-10): once the PRIMARY screen is live + connectivity
  // ok, offer an opt-in "Add second screen" banner (non-blocking) if this agent
  // may record more than one screen and hasn't added/dismissed it yet. This
  // replaces the surprise double browser prompt (Fernando 2026-09-10).
  const canOfferSecondScreen =
    required === true && !bypassed && hasStream && conn === 'ok' &&
    maxScreens > 1 && extraCount < (maxScreens - 1) && !secondScreenDismissed;

  // Nothing to enforce.
  if (required !== true) return null;
  // Agent proceeded unrecorded via an allowed bypass -> gate clears.
  if (bypassed) return null;
  // Stream present AND connectivity OK -> either offer the 2nd screen banner
  // (non-blocking) or clear the gate entirely.
  if (hasStream && conn === 'ok') {
    if (canOfferSecondScreen) {
      return (
        <SecondScreenBanner
          busy={addingScreen}
          onAdd={handleAddScreen}
          onDismiss={() => setSecondScreenDismissed(true)}
        />
      );
    }
    return null;
  }

  // ---- Connectivity-failure screen (stream present, Azure unreachable) ----
  if (hasStream && conn === 'failed') {
    return (
      <ConnectivityFlag
        connError={connError}
        bypassAllowed={bypassAllowed}
        busy={busy}
        error={error}
        onRetry={() => { setConn(null); }}
        onLogout={onLogout}
        onProceed={handleBypass}
      />
    );
  }

  // ---- Testing screen (stream present, preflight in flight) ----
  if (hasStream && (conn === 'testing' || conn === null)) {
    return <ConnectivityTesting />;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cx-screenshare-gate-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: 'var(--cx-panel, #fff)',
          color: 'var(--cx-text, #0f172a)',
          borderRadius: 8,
          padding: 24,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
          border: '1px solid var(--cx-border, #e4e7eb)',
        }}
      >
        <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🎥</div>
        <h2
          id="cx-screenshare-gate-title"
          style={{
            fontSize: 20,
            fontWeight: 700,
            textAlign: 'center',
            margin: 0,
            marginBottom: 12,
            color: 'var(--cx-text, #0f172a)',
          }}
        >
          Screen Recording is Required
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
          As part of your role on this platform, <strong>every call you handle must be
          screen-recorded alongside its voice recording.</strong> Screen recordings are
          used for quality assurance, coaching, and compliance auditing.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
          Click <strong>Enable Screen Recording</strong> below, and when your browser
          asks what to share, choose <strong>your entire screen</strong> (not a single
          window or tab). The recording will start automatically each time you take a
          call and will stop automatically when the call ends.
        </p>
        <p style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 20, opacity: 0.75 }}>
          You will only be prompted once per session. If you deny the prompt, you will
          not be able to take calls. If you don't wish to be recorded today, please
          use the Log out button instead.
        </p>

        {error && (
          <div
            style={{
              background: '#FEE2E2',
              color: '#991B1B',
              border: '1px solid #FCA5A5',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={onLogout}
            disabled={busy}
            style={{
              padding: '10px 16px',
              border: '1px solid var(--cx-border, #e4e7eb)',
              background: 'var(--cx-panel, #fff)',
              color: 'var(--cx-text, #0f172a)',
              borderRadius: 6,
              fontSize: 14,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >
            🚪 Log out
          </button>
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            style={{
              flex: 1,
              marginLeft: 8,
              padding: '10px 16px',
              border: '1px solid #16A34A',
              background: '#16A34A',
              color: '#fff',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Requesting…' : '🎥 Enable Screen Recording'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// Connectivity preflight sub-views (2026-07-28)
// ---------------------------------------------------------------------

const OVERLAY_STYLE = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'rgba(15, 23, 42, 0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const CARD_STYLE = {
  maxWidth: 520,
  width: '100%',
  background: 'var(--cx-panel, #fff)',
  color: 'var(--cx-text, #0f172a)',
  borderRadius: 8,
  padding: 24,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
  border: '1px solid var(--cx-border, #e4e7eb)',
};

// Multi-monitor (2026-09-10): small NON-blocking banner offering the agent an
// opt-in second screen. Bottom-right toast; the agent can add or dismiss. Does
// NOT overlay/block the softphone (calls flow normally with one screen).
function SecondScreenBanner({ busy, onAdd, onDismiss }) {
  return (
    <div
      role="dialog"
      aria-label="Add second screen"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9998,
        maxWidth: 340,
        background: 'var(--cx-panel, #fff)',
        color: 'var(--cx-text, #0f172a)',
        border: '1px solid var(--cx-border, #e4e7eb)',
        borderRadius: 10,
        boxShadow: '0 12px 30px rgba(0,0,0,0.28)',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 22 }}>🖥️🖥️</span>
        <strong style={{ fontSize: 15 }}>Using two monitors?</strong>
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.45, margin: '0 0 12px', opacity: 0.85 }}>
        Your first screen is being recorded. If you work across a second monitor,
        add it so <strong>both screens</strong> are captured on every call. You'll
        get one more share prompt — pick your <strong>other monitor</strong>.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          style={{
            padding: '8px 12px', border: '1px solid var(--cx-border, #e4e7eb)',
            background: 'var(--cx-panel, #fff)', color: 'var(--cx-text, #0f172a)',
            borderRadius: 6, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.5 : 1,
          }}
        >
          Just one screen
        </button>
        <button
          type="button"
          onClick={onAdd}
          disabled={busy}
          style={{
            padding: '8px 12px', border: '1px solid #16A34A', background: '#16A34A',
            color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Requesting…' : '➕ Add second screen'}
        </button>
      </div>
    </div>
  );
}

function ConnectivityTesting() {
  return (
    <div role="dialog" aria-modal="true" style={OVERLAY_STYLE}>
      <div style={CARD_STYLE}>
        <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🔌</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', margin: 0, marginBottom: 12 }}>
          Checking screen-recording connectivity…
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, textAlign: 'center', opacity: 0.8 }}>
          Verifying that screen recordings can reach secure storage from your
          network. This takes just a moment.
        </p>
      </div>
    </div>
  );
}

function ConnectivityFlag({
  connError, bypassAllowed, busy, error, onRetry, onLogout, onProceed,
}) {
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="cx-conn-flag-title" style={OVERLAY_STYLE}>
      <div style={CARD_STYLE}>
        <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🚫</div>
        <h2
          id="cx-conn-flag-title"
          style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', margin: 0, marginBottom: 12, color: '#B91C1C' }}
        >
          Screen Recording Cannot Connect
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
          Your screen recording <strong>cannot reach secure storage from your
          current network.</strong> This usually means a firewall, VPN, or
          proxy on your network is blocking the connection.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12, opacity: 0.85 }}>
          Please contact your IT / network team and ask them to allow outbound
          HTTPS (port 443) to <strong>continuumcx.blob.core.windows.net</strong>,
          then click <strong>Retry test</strong>.
        </p>

        <div
          style={{
            background: '#FEF2F2', color: '#7F1D1D', border: '1px solid #FCA5A5',
            borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 16,
            fontFamily: 'monospace', wordBreak: 'break-word',
          }}
        >
          {connError || 'connectivity_failed'}
        </div>

        {!bypassAllowed && (
          <p style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 16, fontWeight: 600 }}>
            You cannot log in until screen-recording connectivity is restored.
          </p>
        )}

        {error && (
          <div style={{
            background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5',
            borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 12,
          }}>
            {String(error)}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onLogout}
            disabled={busy}
            style={{
              padding: '10px 16px', border: '1px solid var(--cx-border, #e4e7eb)',
              background: 'var(--cx-panel, #fff)', color: 'var(--cx-text, #0f172a)',
              borderRadius: 6, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >
            🚪 Log out
          </button>
          <button
            type="button"
            onClick={onRetry}
            disabled={busy}
            style={{
              flex: 1, minWidth: 140, padding: '10px 16px', border: '1px solid #2563EB',
              background: '#2563EB', color: '#fff', borderRadius: 6, fontSize: 14,
              fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
            }}
          >
            🔄 Retry test
          </button>
          {bypassAllowed && (
            <button
              type="button"
              onClick={onProceed}
              disabled={busy}
              style={{
                width: '100%', marginTop: 8, padding: '10px 16px',
                border: '1px solid #D97706', background: '#F59E0B', color: '#7C2D12',
                borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Please wait…' : '⚠️ Proceed without screen recording'}
            </button>
          )}
        </div>

        {bypassAllowed && (
          <p style={{ fontSize: 11, lineHeight: 1.5, marginTop: 12, opacity: 0.7 }}>
            Proceeding without screen recording will be logged for compliance
            review. Your calls today may not be screen-recorded.
          </p>
        )}
      </div>
    </div>
  );
}
